import { store, buildMeasurement } from '~/utility/BinSentry-ui-utility';
import moment from 'moment';
import '../common/bn-surface';
import '../common/bn-property';
import '../common/bn-icon';
import '../common/bn-radio-input';
import { LitElement, html, css } from 'lit-element';
import { renderIf } from '../utility/lit-entity';
import { StoreMixin } from '../utility/store-mixin';
import { sharedStyles } from '../styles/shared-styles';

class IssueViewer extends StoreMixin(LitElement) {
  constructor() {
    super();
    this._stateChanged(store.getState());
    this.correctedDimensions = {};

    this.preferences = {
      dimension: buildMeasurement('dimension', 'dimension:metric:meters'),
      weight: buildMeasurement('weight', 'weight:metric:tonnes'),
    };
  }

  static get properties() {
    return {
      query: { type: Object },
      preferences: { type: Object },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        margin-top: 0;
        margin: 0 auto;
        padding: 1rem 3rem;
        display: flex;
        flex-direction: column;
        max-width: 1200px;
        transition: background-color .2s, box-shadow .2s;
      }
      @media(min-width: 1300px) {
        :host {
          background-color: var(--gray-40);
          box-shadow: 0 0 12px #00000082;
        }
      }
      bn-radio-input {
        --bn-radio-input-display: flex;
        --bn-radio-input-padding: .25rem .5rem;
      }
      bn-surface {
        margin-left: 0;
        margin-right: 0;
      }
      h1 {
        font-size: 3rem;
      }
      .flex {
        display: flex;
        flex-wrap: wrap;
      }
      .details bn-property {
        margin-top: 1rem;
        overflow: hidden;
      }
      .details a {
        white-space: normal;
      }
      .title-container {
        justify-content: space-between;
        align-items: center;
      }
      .centered {
        justify-content: center;
        align-items: center;
        text-align: center;
      }
      .preferences {
        margin-top: 0;
      }
      .preferences div {
        margin-right: 1rem;
      }
      .preferences h4, .preferences h3 {
        margin-left: .5rem;
      }
      .top-property {
        align-items: center;
        margin-bottom: 1rem;
      }
      .top-property * + * {
        margin-left: 1rem;
      }
      .row {
        flex-direction: row;
      }
      .col {
        flex-direction: column;
      }
      bn-surface.inactive {
        user-select: none;
        opacity: .75;
      }
      bn-surface .title span {
        border: 2px solid;
        border-radius: 4px;
        padding: .25rem;
        margin-left: 1rem;
      }
      bn-surface .yes {
        color: var(--green-20);
        border-color: var(--green-20);
      }
      bn-surface .no {
        color: var(--red-20);
        border-color: var(--red-20);
      }
      .content {
        margin-top: 1rem;
        flex-wrap: wrap;
      }
      .content > .flex.col {
        margin: 0rem 1rem 1rem 0;
        padding: 1.5rem 2rem 2rem 2rem;
        border-radius: 4px;
        background-color: var(--gray-40);
        border: 1px solid var(--gray-50);
      }
      .content > .flex.col.difference {
        background-color: var(--blue-40);
        border-color: var(--blue-50);
        border-style: dashed;
      }
      .content h4 {
        text-align: center;
        font-size: 1.125rem;
        margin-top: 0;
        margin-bottom: 1rem;
      }
      .content bn-property {
        margin: .25rem;
      }
      bin-profile-svg {
        width: 200px;
        height: 250px;
      }
    `];
  }

  _renderTitle() {
    return html`<h1><bn-icon name="fas fa-binoculars"></bn-icon> Issue Viewer</h1>`;
  }

  render() {
    if (!this.query || this._blankObject(this.query)) {
      return this._renderNoDataFound();
    }

    const levelIssueTitle = !this._blankObject(this.correctedLevel || {}) ? `Level Issues on ${moment(this.disputedLevelProperties.publishedAt).format('MMM D, h:mm a')}` : 'Level Issues?';

    return html`
      <header class="flex row title-container">
        ${this._renderTitle()}

        <bn-surface class="flex col preferences">
          <h3>Preferences</h3>
          <div class="flex row preferences">
            <div>
              <h4>Dimensions</h4>
              <bn-radio-input
                .values="${[{ title: 'Metres', value: 'distance:metric:meters', checked: true }, { title: 'Inches', value: 'distance:imperial:inches' }]}"
                @input="${(e) => this._changePreference(e.target.value, 'dimension')}"
              ></bn-radio-input>
            </div>

            <div>
              <h4>Weight</h4>
              <bn-radio-input
                .values="${[{ title: 'Metric Tonnes', value: 'weight:metric:tonnes', checked: true }, { title: 'Tons', value: 'weight:imperial:short_tons' }]}"
                @input="${(e) => this._changePreference(e.target.value, 'weight')}"
              ></bn-radio-input>
            </div>
          </div>
        </bn-surface>
      </header>

      <bn-surface class="details">
        <h2>Details</h2>
        <a href="/bins/show/overview?binId=${encodeURIComponent(this.query.binId)}">View Bin</a>
        <bn-property stacked label="Bin Name">
          ${this.query.binPathName}
        </bn-property>
        <bn-property stacked label="Bin Id">
          ${this.query.binId}
        </bn-property>
        <bn-property stacked label="Reported At">
          ${moment(this.query.reportedAt).format('MMM D, h:mm a')}
        </bn-property>
      </bn-surface>

      ${renderIf(this.binProperties.role !== 'ingredient', () => html`
        ${this._renderQuestionGroup('Physical Issues?', this.physicalIssue)}
        ${this._renderQuestionGroup(levelIssueTitle, !this._blankObject(this.correctedLevel || {}), () => html`
          <div class="flex col">
            <h4>Suggestion</h4>
            <div class="flex row data">
              <div class="flex col">
                <bin-profile-svg nodevice .bindimensions="${this.binProperties.dimensions}" .binlevel="${this.correctedLevel}" .binvolumes="${{ total: 1 }}"></bin-profile-svg>
              </div>
              <div class="flex col">
                <bn-property stacked label="Feed Height">${this.preferences.dimension.html({ dimension: this.correctedLevel.feedHeight })}</bn-property>
                <bn-property stacked label="Feed Weight">${this.preferences.weight.html({ weight: this.correctedLevel.weight })}</bn-property>
              </div>
            </div>
          </div>

          <div class="flex col">
            <h4>Current</h4>
            <div class="flex row">
              <div class="flex col">
                <bin-profile-svg nodevice .bindimensions="${this.binProperties.dimensions}" .binlevel="${this.disputedLevelProperties}" .binvolumes="${{ total: 1 }}"></bin-profile-svg>
              </div>
              <div class="flex col">
                <bn-property stacked label="Feed Height">${this.preferences.dimension.html({ dimension: this.disputedLevelProperties.feedHeight })}</bn-property>
                <bn-property stacked label="Feed Weight">${this.preferences.weight.html({ weight: this.disputedLevelProperties.weight })}</bn-property>
              </div>
            </div>
          </div>

          <div class="flex col difference">
            <h4>Difference</h4>
            <div class="flex row">
              <div class="flex col">
                <bn-property stacked label="Feed Height">${this.preferences.dimension.html({ dimension: this.correctedLevel.feedHeight - this.disputedLevelProperties.feedHeight })}</bn-property>
                <bn-property stacked label="Feed Weight">${this.preferences.weight.html({ weight: this.correctedLevel.weight - this.disputedLevelProperties.weight })}</bn-property>
              </div>
            </div>
          </div>
        `)}

        ${this._renderQuestionGroup('Measurement Issues?', !this._blankObject(this.correctedDimensions), () => html`
          <div class="flex col">
            <h4>Suggestion</h4>
            <div class="flex row">
              <div class="flex col">
                <bin-profile-svg nodevice .bindimensions="${this.correctedDimensions}"></bin-profile-svg>
              </div>
              <div class="flex col">
                <bn-property stacked label="Top Height">${this.preferences.dimension.html({ dimension: this.correctedDimensions.top.height })}</bn-property>
                <bn-property stacked label="Body Height">${this.preferences.dimension.html({ dimension: this.correctedDimensions.body.height })}</bn-property>
                <bn-property stacked label="Hopper Height">${this.preferences.dimension.html({ dimension: this.correctedDimensions.hopper.height })}</bn-property>
                <bn-property stacked label="Body Diameter">${this.preferences.dimension.html({ dimension: this.correctedDimensions.body.radius * 2 })}</bn-property>
                <bn-property stacked label="Ring Count">${this.preferences.dimension.html({ dimension: this.correctedDimensions.ringCount })}</bn-property>
                <bn-property stacked label="Total Height (calculated)">${this.preferences.dimension.html({ dimension: this.correctedDimensions.totalHeight })}</bn-property>
              </div>
            </div>
          </div>
          <div class="flex col">
            <h4>Current</h4>
            <div class="flex row">
              <div class="flex col">
                <bin-profile-svg nodevice .bindimensions="${this.binProperties.dimensions}"></bin-profile-svg>
              </div>
              <div class="flex col">
                <bn-property stacked label="Top Height">${this.preferences.dimension.html({ dimension: this.binProperties.dimensions.top.height })}</bn-property>
                <bn-property stacked label="Body Height">${this.preferences.dimension.html({ dimension: this.binProperties.dimensions.body.height })}</bn-property>
                <bn-property stacked label="Hopper Height">${this.preferences.dimension.html({ dimension: this.binProperties.dimensions.hopper.height })}</bn-property>
                <bn-property stacked label="Body Diameter">${this.preferences.dimension.html({ dimension: this.binProperties.dimensions.body.radius * 2 })}</bn-property>
                <bn-property stacked label="Ring Count">${this.preferences.dimension.html({ dimension: this.binProperties.dimensions.ringCount })}</bn-property>
                <bn-property stacked label="Total Height (calculated)">${this.preferences.dimension.html({ dimension: this.binProperties.dimensions.totalHeight })}</bn-property>
              </div>
            </div>
          </div>
          <div class="flex col difference">
            <h4>Difference</h4>
            <div class="flex col">
              <bn-property stacked label="Top Height">${this.preferences.dimension.html({ dimension: this.dimensionsDifference.top.height })}</bn-property>
              <bn-property stacked label="Body Height">${this.preferences.dimension.html({ dimension: this.dimensionsDifference.body.height })}</bn-property>
              <bn-property stacked label="Hopper Height">${this.preferences.dimension.html({ dimension: this.dimensionsDifference.hopper.height })}</bn-property>
              <bn-property stacked label="Body Radius">${this.preferences.dimension.html({ dimension: this.dimensionsDifference.body.radius })}</bn-property>
              <bn-property stacked label="Ring Count">${this.preferences.dimension.html({ dimension: this.dimensionsDifference.ringCount })}</bn-property>
              <bn-property stacked label="Total Height (calculated)">${this.preferences.dimension.html({ dimension: this.dimensionsDifference.totalHeight })}</bn-property>
            </div>
          </div>
        `)}
      `)}
      ${this._renderQuestionGroup('Comments?', this.query.comments, () => this.query.comments)}
    `;
  }

  _changePreference(preference, category) {
    this.preferences[category] = buildMeasurement(category, preference);
    this.requestUpdate();
  }

  _renderQuestionGroup(title, hasData, innerHTMLFunction) {
    return html`
      <bn-surface class="${hasData ? 'active' : 'inactive'}">
        <h2 class="title">
          ${title} ${hasData ? html`<span class="yes">Yes</span>` : html`<span class="no">No</span>`}
        </h2>

        ${renderIf(hasData && innerHTMLFunction, () => html`
          <div class="content flex row">${innerHTMLFunction()}</div>
        `)}
      </bn-surface>
    `;
  }

  _renderNoDataFound() {
    return html`
      <header class="flex row title-container centered">
        ${this._renderTitle()}
      </header>

      <bn-surface class="details centered">
        <h3>No valid issue data found in the url query parameters</h3>
        <p>Check to make sure the url is correct and retry your request.</p>
      </bn-surface>
    `;
  }

  _blankObject(object) {
    const values = Object.values(object);
    return values.length === 0 || !values.find((values) => values !== '');
  }

  _stateChanged({ query }) {
    if (!query || this._blankObject(query)) {
      this.query = undefined;
      return;
    }

    this.query = query;

    try {
      this.physicalIssue = this.query.physicalIssue !== undefined && JSON.parse(this.query.physicalIssue);
      this.binProperties = this.query.binProperties && JSON.parse(this.query.binProperties);

      const correctedDimensions = this.query.correctedDimensions ? JSON.parse(this.query.correctedDimensions) : {};
      if (!this._blankObject(correctedDimensions)) {
        // Intentionally parsed twice, want a full duplicate of the object
        this.correctedDimensions = JSON.parse(this.query.binProperties).dimensions;
        this.correctedDimensions.top.height = correctedDimensions.topHeight;
        this.correctedDimensions.body.height = correctedDimensions.bodyHeight;
        this.correctedDimensions.hopper.height = correctedDimensions.hopperHeight;
        this.correctedDimensions.totalHeight = this.correctedDimensions.top.height + this.correctedDimensions.body.height + this.correctedDimensions.hopper.height;
        this.correctedDimensions.body.radius = correctedDimensions.binRadius;
        this.correctedDimensions.ringCount = correctedDimensions.ringCount;

        this.dimensionsDifference = JSON.parse(this.query.binProperties).dimensions;
        this.dimensionsDifference.top.height = this.correctedDimensions.top.height - this.dimensionsDifference.top.height;
        this.dimensionsDifference.body.height = this.correctedDimensions.body.height - this.dimensionsDifference.body.height;
        this.dimensionsDifference.hopper.height = this.correctedDimensions.hopper.height - this.dimensionsDifference.hopper.height;
        this.dimensionsDifference.totalHeight = this.correctedDimensions.totalHeight - this.dimensionsDifference.totalHeight;
        this.dimensionsDifference.body.radius = this.correctedDimensions.body.radius - this.dimensionsDifference.body.radius;
        this.dimensionsDifference.ringCount = this.correctedDimensions.ringCount - this.dimensionsDifference.ringCount;
      }

      this.correctedLevel = this.query.correctedLevel && JSON.parse(this.query.correctedLevel);
      this.disputedLevelProperties = this.query.disputedLevelProperties && JSON.parse(this.query.disputedLevelProperties);
      this.requestUpdate();
    } catch (e) {
      this.query = undefined;
    }
  }
}

customElements.define('issue-viewer', IssueViewer);
